import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { claritySession, track, savedReport, saveReport, clearReport, tokenHeader } from "../lib/clarity";
import ClarityIntro from "../components/clarity/ClarityIntro";
import ClarityFlow from "../components/clarity/ClarityFlow";
import ClarityPreview from "../components/clarity/ClarityPreview";
import ContactGate from "../components/clarity/ContactGate";
import ClarityReport from "../components/clarity/ClarityReport";
import { LockedBlur } from "../components/LockedBlur";
import { useAppAuthed } from "../lib/gate";

const DRAFT = "ex_clarity_draft";

export default function ClarityScore() {
  const [questions, setQuestions] = useState([]);
  const [stage, setStage] = useState("intro");
  const [answers, setAnswersRaw] = useState(() => { try { return JSON.parse(sessionStorage.getItem(DRAFT) || "{}"); } catch { return {}; } });
  const [data, setData] = useState(null);
  const [token, setToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const saved = savedReport();
  const authed = useAppAuthed();

  const setAnswers = (a) => { setAnswersRaw(a); sessionStorage.setItem(DRAFT, JSON.stringify(a)); };
  const top = () => window.scrollTo({ top: 0, behavior: "smooth" });

  useEffect(() => {
    api.get("/clarity/questions").then((r) => setQuestions(r.data.questions)).catch(() => toast.error("Could not load the assessment."));
    track("page_view");
  }, []);

  const loadReport = async (aid, tok, next = "report") => {
    const r = await api.get(`/clarity/${aid}/report`, tokenHeader(tok));
    setData(r.data); setToken(tok); setAnswersRaw(r.data.answers); setStage(next); top();
    return r.data;
  };

  const resume = () => loadReport(saved.assessmentId, saved.token).then(() => track("report_view")).catch(() => { clearReport(); toast.error("That report is no longer available."); });

  const score = async (a, tier) => {
    setSubmitting(true);
    try {
      const body = { session_id: claritySession(), tier, answers: a, assessment_id: tier === "deep" ? data?.assessment_id : undefined };
      const r = await api.post("/clarity/score", body, token ? tokenHeader(token) : undefined);
      const tok = r.data.token || token;
      saveReport(r.data.assessment_id, tok);
      setToken(tok); setData(r.data);
      track(tier === "deep" ? "deep_complete" : "complete");
      setStage(tier === "deep" || r.data.contact_captured ? "report" : "preview"); top();
      if (tier === "deep" && r.data.contact_captured) setTimeout(() => loadReport(r.data.assessment_id, tok).catch(() => {}), 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not calculate your score.");
    } finally { setSubmitting(false); }
  };

  const afterContact = async () => {
    setData((d) => ({ ...d, contact_captured: true }));
    setStage("report"); top(); track("report_view");
    loadReport(data.assessment_id, token).catch(() => {});
  };

  const retake = () => { clearReport(); setAnswers({}); setData(null); setToken(null); setStage("intro"); top(); };
  const quickCount = questions.filter((q) => q.tier === "quick" && q.type !== "top_pick").length;

  return (
    <div data-testid="clarity-score-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-28 lg:pt-36 pb-24">
        {stage === "intro" && <ClarityIntro quickCount={quickCount || 16} hasSaved={!!saved} onResume={resume} onStart={() => { track("start"); setStage("quick"); top(); }} />}
        {stage === "quick" && questions.length > 0 && <ClarityFlow questions={questions} tier="quick" answers={answers} setAnswers={setAnswers} submitting={submitting} onComplete={(a) => score(a, "quick")} />}
        {stage === "deep" && <ClarityFlow questions={questions} tier="deep" answers={answers} setAnswers={setAnswers} submitting={submitting} onComplete={(a) => score(a, "deep")} />}
        {stage === "preview" && data && (
          <div className="space-y-12">
            <LockedBlur testid="clarity-preview-locked" label="Your Clarity Score is ready"><ClarityPreview result={data.result} /></LockedBlur>
            {authed && <ContactGate assessmentId={data.assessment_id} token={token} onDone={afterContact} onSkip={() => { setStage("report"); top(); track("report_view"); }} />}
          </div>
        )}
        {stage === "contact" && data && (
          <div className="max-w-3xl mx-auto">
            <ContactGate assessmentId={data.assessment_id} token={token} onDone={afterContact} onSkip={() => setStage("report")} />
          </div>
        )}
        {stage === "report" && data && (
          <LockedBlur testid="clarity-report-locked" label="Your report is ready">
            <ClarityReport data={data} token={token} onRetake={retake} onNeedContact={() => { setStage("contact"); top(); }}
              onDeep={() => { track("deep_start"); setStage("deep"); top(); }} />
          </LockedBlur>
        )}
      </main>
      <Footer />
    </div>
  );
}
